import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Modal, Input, message } from 'antd';
import { Search } from 'lucide-react';
import { api, fmt } from '../api';
import { CATEGORY_KO } from '../labels';
import MerchantDetailModal from '../components/MerchantDetailModal';
import { SkeletonTable } from '../components/Skeleton';

type Merchant = {
  mid: string; name: string; start_date: string; term_days: number;
  deposit_amount: number; purchase_amount: number; fee_rate: number;
  category: 'repay' | 'normal' | 'bad';
  effective_category: 'repay' | 'normal' | 'bad';
  auto_bad: boolean;
  active: number;
  payout_method: 'bank' | 'wallet' | null;
  people_id: string | null;
  payout_bank: string | null; payout_account: string | null; payout_holder: string | null;
  last_sales_date: string | null;
  days_since_last_sales: number | null;
  remaining_days: number | null;
  remaining_amount: number | null;
  cumulative_repaid: number | null;
  latest_settle_date: string | null;
  ceo_name: string | null;
  contact_phone: string | null;
  business_no: string | null;
  business_address: string | null;
  received_noti_kinds: string | null;
  tx_count?: number;
  has_device?: number;
  device_return_status?: 'unreturned' | 'returned' | 'not_applicable';
  device_return_date?: string | null;
};

// 카테고리-결제수신 정합성: 반환은 할인매매 결제 수신, 일반은 결제 수신을 받아야 함.
// 어긋나면 정산 화면에서 매출이 빠져 대시보드와 안 맞음 → 운영자에게 시각적 경고.
// 문서 08_28 §3: "분류 불일치" → "분류 확인 필요" (사용자 용어).
function categoryNotiMismatch(m: Merchant): { title: string; detail: string; current: string; received: string } | null {
  const kinds = (m.received_noti_kinds ?? '').split(',').filter(Boolean);
  if (kinds.length === 0) return null;            // 노티 0건은 별도 "결제 수신내역 없음" 칩에서 다룸
  const hasDiscount = kinds.includes('merchant_discount');
  const hasMerchant = kinds.includes('merchant');
  const catKo = m.category === 'normal' ? '일반' : m.category === 'repay' ? '반환' : m.category === 'bad' ? '악성' : String(m.category);
  if (m.category === 'normal' && hasDiscount && !hasMerchant) {
    return {
      title: '분류 확인 필요',
      current: '일반',
      received: '할인매매 결제 수신',
      detail: '현재 「일반」으로 등록되어 있으나 할인매매 결제만 수신되고 있어 고객분류 또는 결제 연결정보 확인이 필요합니다.',
    };
  }
  if (m.category === 'repay' && hasMerchant && !hasDiscount) {
    return {
      title: '분류 확인 필요',
      current: '반환',
      received: '일반 결제 수신',
      detail: '현재 「반환」으로 등록되어 있으나 일반 결제만 수신되고 있어 고객분류 또는 결제 연결정보 확인이 필요합니다.',
    };
  }
  return null;
}

export default function Merchants() {
  const [data, setData] = useState<Merchant[]>([]);
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState<'all'|'repay'|'normal'|'bad'>('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api<{ data: Merchant[] }>('GET', '/merchants');
      setData(r.data ?? []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const [methodFilter, setMethodFilter] = useState<'all'|'bank'|'wallet'>('all');
  // 상태 필터: 노티 미수신(last_sales_date == null) / 분류 불일치(categoryNotiMismatch)
  const [statusFilter, setStatusFilter] = useState<'all'|'no_noti'|'mismatch'>('all');
  // 단말기 반납 필터 (문서4 §5)
  const [deviceReturnFilter, setDeviceReturnFilter] = useState<'all'|'unreturned'|'returned'|'not_applicable'>('all');
  const [detailMid, setDetailMid] = useState<string | null>(null);

  // "결제 수신내역 없음" = 운영 결제 수신이 한 번도 없음 (모든 유형). 카테고리와 무관.
  const hasNoNoti = (m: Merchant) => (m.tx_count ?? 0) === 0;
  const hasMismatch = (m: Merchant) => !!categoryNotiMismatch(m);

  const filtered = data.filter(m => {
    // 필터는 effective_category 기준 — 약정 만기 + 잔여액 있는 상환은 자동 악성으로 잡힘.
    const eff = m.effective_category ?? m.category;
    if (catFilter !== 'all' && eff !== catFilter) return false;
    if (statusFilter === 'no_noti' && !hasNoNoti(m)) return false;
    if (statusFilter === 'mismatch' && !hasMismatch(m)) return false;
    if (methodFilter !== 'all' && (m.payout_method ?? 'bank') !== methodFilter) return false;
    if (deviceReturnFilter !== 'all' && (m.device_return_status ?? 'unreturned') !== deviceReturnFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    // 사업자등록번호는 하이픈 포함/미포함 모두 매칭.
    const bnoDigits = (m.business_no ?? '').replace(/\D/g, '');
    const qDigits = s.replace(/\D/g, '');
    return m.mid.toLowerCase().includes(s)
        || m.name.toLowerCase().includes(s)
        || (m.payout_holder?.toLowerCase().includes(s) ?? false)
        || (m.people_id?.toLowerCase().includes(s) ?? false)
        || (m.ceo_name?.toLowerCase().includes(s) ?? false)
        || (m.business_no?.toLowerCase().includes(s) ?? false)
        || (qDigits.length >= 3 && bnoDigits.includes(qDigits));
  });

  const noNotiCount = data.filter(hasNoNoti).length;
  const mismatchCount = data.filter(hasMismatch).length;
  // 문서 08_31 §6: 필터 옵션별 건수 표시.
  const catCount = (v: 'repay'|'normal'|'bad') => data.filter(m => (m.effective_category ?? m.category) === v).length;
  const methodTypeCount = (v: 'bank'|'wallet') => data.filter(m => (m.payout_method ?? 'bank') === v).length;
  const deviceCount = (v: 'unreturned'|'returned'|'not_applicable') => data.filter(m => (m.device_return_status ?? 'unreturned') === v).length;

  const [renameFor, setRenameFor] = useState<{ mid: string; name: string; value: string } | null>(null);
  const [renaming, setRenaming] = useState(false);

  const remove = (mid: string, name: string) => {
    Modal.confirm({
      title: `'${name}' 가맹점 삭제`,
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div><b>MID:</b> <span style={{ fontFamily: 'monospace' }}>{mid}</span></div>
          <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: 12 }}>
            거래·정산·이체 이력이 있는 가맹점은 삭제 대신 <b>비활성화</b>를 권장합니다.
          </div>
        </div>
      ),
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          await api('DELETE', `/merchants/${encodeURIComponent(mid)}`);
          message.success('삭제됨');
          await load();
        } catch (e: any) {
          Modal.error({ title: '삭제 실패', content: e?.message ?? '오류', okText: '확인' });
        }
      },
    });
  };

  const toggleActive = (mid: string, name: string, currentActive: number) => {
    const willActivate = !currentActive;
    Modal.confirm({
      title: `'${name}' 가맹점 ${willActivate ? '활성화' : '비활성화'}`,
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div><b>MID:</b> <span style={{ fontFamily: 'monospace' }}>{mid}</span></div>
          <div style={{ marginTop: 8 }}>
            {willActivate
              ? '이 가맹점이 정산 화면 등에 노출됩니다. 계속하시겠습니까?'
              : '이 가맹점은 정산 화면에서 빠지고 결제가맹점 추가 목록에 자동 노출됩니다. 계속하시겠습니까?'}
          </div>
        </div>
      ),
      okText: willActivate ? '활성화' : '비활성화',
      okType: willActivate ? 'primary' : 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          await api('PATCH', `/merchants/${encodeURIComponent(mid)}/active`, { active: willActivate });
          message.success(willActivate ? '활성화됨' : '비활성화됨');
          await load();
        } catch (e: any) {
          Modal.error({ title: `${willActivate ? '활성화' : '비활성화'} 실패`, content: e?.message ?? '오류', okText: '확인' });
        }
      },
    });
  };

  const renameMid = (mid: string, name: string) => {
    setRenameFor({ mid, name, value: mid });
  };
  const doRename = async () => {
    if (!renameFor) return;
    const newMid = renameFor.value.trim();
    if (!newMid || newMid === renameFor.mid) { setRenameFor(null); return; }
    if (!/^[a-zA-Z0-9._-]+$/.test(newMid)) {
      Modal.warning({ title: 'MID 형식 오류', content: 'MID는 영문/숫자/._- 만 허용됩니다.', okText: '확인' });
      return;
    }
    setRenaming(true);
    try {
      await api('PATCH', `/merchants/${encodeURIComponent(renameFor.mid)}/rename`, { new_mid: newMid });
      const oldMid = renameFor.mid;
      setRenameFor(null);
      await load();
      Modal.success({ title: 'MID 변경 완료', content: `${oldMid} → ${newMid}`, okText: '확인' });
    } catch (e: any) {
      if (e?.message === 'new_mid_exists') {
        Modal.error({
          title: 'MID 변경 실패',
          content: '새 MID로 이미 등록된 가맹점이 있습니다. 그 가맹점을 먼저 삭제하거나 흡수해야 합니다.',
          okText: '확인',
        });
      } else {
        Modal.error({ title: 'MID 변경 실패', content: e?.message ?? '오류', okText: '확인' });
      }
    } finally { setRenaming(false); }
  };

  return (
    <div>
      <h2>가맹점 관리</h2>
      <div className="card">
        <div className="toolbar">
          <div style={{ flex: 1, minWidth: 240 }}>
            <label>검색</label>
            <input placeholder="MID · 가맹점명 · 예금주 · 피플 ID · 대표자명 · 사업자등록번호" value={q} onChange={e => setQ(e.target.value)} style={{ width: '100%' }} />
          </div>
          <div>
            <label>고객분류</label>
            <select value={catFilter} onChange={e => setCatFilter(e.target.value as any)}
                    style={{ minWidth: 130 }}>
              <option value="all">전체 ({data.length})</option>
              <option value="repay">반환 ({catCount('repay')})</option>
              <option value="normal">일반 ({catCount('normal')})</option>
              <option value="bad">악성 ({catCount('bad')})</option>
            </select>
          </div>
          <div>
            <label>정산방식</label>
            <select value={methodFilter} onChange={e => setMethodFilter(e.target.value as any)}>
              <option value="all">전체 ({data.length})</option>
              <option value="bank">은행정산 ({methodTypeCount('bank')})</option>
              <option value="wallet">지갑정산 ({methodTypeCount('wallet')})</option>
            </select>
          </div>
          <div>
            <label>상태</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)}>
              <option value="all">전체 ({data.length})</option>
              <option value="no_noti">결제 수신내역 없음 ({noNotiCount.toLocaleString('ko-KR')})</option>
              <option value="mismatch">분류 확인 필요 ({mismatchCount.toLocaleString('ko-KR')})</option>
            </select>
          </div>
          <div>
            <label>단말기 반납</label>
            <select value={deviceReturnFilter} onChange={e => setDeviceReturnFilter(e.target.value as any)}>
              <option value="all">전체 ({data.length})</option>
              <option value="unreturned">미반납 ({deviceCount('unreturned')})</option>
              <option value="returned">반납완료 ({deviceCount('returned')})</option>
              <option value="not_applicable">해당없음 ({deviceCount('not_applicable')})</option>
            </select>
          </div>
          <div style={{ marginLeft: 'auto', textAlign: 'right', alignSelf: 'flex-end' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>총 가맹점</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--leaf-800)' }}>
              {data.length.toLocaleString('ko-KR')}개
              {filtered.length !== data.length && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6, fontWeight: 400 }}>
                  (현재 조회 {filtered.length})
                </span>
              )}
            </div>
          </div>
          <Link to="/merchants/new" className="btn">+ 신규 가맹점</Link>
        </div>

        {loading ? <SkeletonTable rows={8} cols={5} showHeader /> : filtered.length === 0 ? (
          <div className="empty-state">
            <Search className="empty-icon" size={36} strokeWidth={1.6} />
            <h4>조회 결과가 없습니다</h4>
            <p>검색어 또는 분류 필터를 변경해 보세요.</p>
          </div>
        ) : (
          <>
            <div className="table-wrap" style={{ '--stick-1-w': '120px', '--stick-2-w': '180px' } as React.CSSProperties}>
              <table>
                <thead><tr>
                  <th className="nowrap stick stick-1" style={{ width: 120, minWidth: 120, maxWidth: 120 }}>MID</th>
                  <th className="stick stick-2" style={{ width: 180, minWidth: 180, maxWidth: 180 }}>가맹점명</th>
                  <th className="nowrap stick stick-3 stick-end">고객분류</th>
                  <th className="num nowrap" style={{ minWidth: 110 }} title="운영 결제 수신 총 건수 (모든 유형). 0건이면 '결제 수신내역 없음'으로 표기.">결제 건수</th>
                  <th className="nowrap" style={{ minWidth: 110 }} title="할인매매정산 결제 수신내역 기준 가장 최근 매출이 발생한 일자">마지막 매출일</th>
                  <th className="num nowrap" style={{ minWidth: 100 }} title="오늘(KST) - 마지막 매출일. 매출이 한 번도 없으면 '-'. 반환 가맹점은 연속 미납일로 해석.">연속 미납일</th>
                  <th className="num nowrap">시작일</th>
                  <th className="num nowrap">기간</th>
                  <th className="num nowrap" title="계약 종료까지 남은 일수 (KST 오늘 - 시작일 기준)">잔여기간</th>
                  <th className="num nowrap">입금액</th>
                  <th className="num nowrap">매입액</th>
                  <th className="num nowrap" title="매입액 - 누적 회수액. 가장 최근 일일정산 스냅샷 기준">당일 잔여액</th>
                  <th className="num nowrap">수수료율</th>
                  <th className="nowrap" style={{ minWidth: 80 }}>정산방식</th>
                  <th className="nowrap" style={{ minWidth: 200, maxWidth: 240 }}>정산은행/계좌 · 피플ID</th>
                  <th className="nowrap" style={{ minWidth: 90, maxWidth: 140 }}>예금주</th>
                  <th className="nowrap" title="단말기 반납 상태 및 반납일" style={{ minWidth: 110 }}>단말기 반납</th>
                  <th className="nowrap" style={{ width: 1 }}>관리</th>
                </tr></thead>
                <tbody>
                  {filtered.map(m => {
                    const days = m.days_since_last_sales;
                    const eff = m.effective_category ?? m.category;
                    // 상환 가맹점만 연속 미납일에 따라 강조. 일반/악성은 단순 표기.
                    const repay = eff === 'repay';
                    const daysStyle: React.CSSProperties = (repay && days != null)
                      ? days >= 7  ? { color: '#7f1d1d', fontWeight: 700, background: '#fee2e2' }
                      : days >= 3  ? { color: '#854d0e', fontWeight: 700, background: '#fef3c7' }
                      : days >= 1  ? { color: 'var(--muted)' }
                      : { color: 'var(--leaf-700)' }
                      : { color: 'var(--muted)' };
                    return (
                      <tr key={m.mid}>
                        <td className="nowrap stick stick-1" style={{ fontFamily: 'monospace', color: 'var(--muted)' }}>{m.mid}</td>
                        <td className="stick stick-2" title={m.name}>
                          <a onClick={(e) => { e.preventDefault(); setDetailMid(m.mid); }}
                             style={{ fontWeight: 600, cursor: 'pointer' }}>
                            {m.name}
                          </a>
                        </td>
                        <td className="nowrap stick stick-3 stick-end">
                          <span className={`chip cat-${eff}`}
                                title={m.auto_bad ? `등록 분류는 '반환'이지만 약정 종료 후 잔여액(${m.remaining_amount?.toLocaleString('ko-KR') ?? 0}원) 남아 있어 자동으로 '악성'으로 분류됨` : undefined}>
                            <span className="dot" />{CATEGORY_KO[eff] ?? eff}
                            {m.auto_bad && <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 700, opacity: 0.85 }}>자동</span>}
                          </span>
                          {(() => {
                            const noTx = m.last_sales_date == null;
                            const mismatch = !noTx ? categoryNotiMismatch(m) : null;
                            if (noTx) {
                              return (
                                <span
                                  style={{
                                    marginLeft: 6, padding: '1px 6px', borderRadius: 4,
                                    fontSize: 10, fontWeight: 700,
                                    background: '#e0f2fe', color: '#075985',
                                  }}
                                  title="PG가 이 MID로 결제 수신내역을 한 번도 보낸 적이 없음 — 등록 MID와 PG MID가 다를 가능성. MID 정정 또는 삭제 필요."
                                >결제 수신내역 없음</span>
                              );
                            }
                            if (mismatch) {
                              return (
                                <span
                                  style={{
                                    marginLeft: 6, padding: '1px 6px', borderRadius: 4,
                                    fontSize: 10, fontWeight: 700,
                                    background: '#fee2e2', color: '#991b1b',
                                    cursor: 'help',
                                  }}
                                  title={`${mismatch.detail}\n\n현재 고객분류: ${mismatch.current} / 실제 수신: ${mismatch.received}`}
                                >분류 확인 필요 ({mismatch.current} · {mismatch.received})</span>
                              );
                            }
                            return null;
                          })()}
                        </td>
                        <td className="num nowrap">
                          {(() => {
                            const cnt = Number(m.tx_count ?? 0);
                            if (cnt === 0) {
                              return <span style={{ color: '#075985', fontWeight: 600 }} title="운영 결제 수신이 한 번도 없음">0건 · 결제 수신내역 없음</span>;
                            }
                            return <span style={{ fontWeight: 600 }}>{cnt.toLocaleString('ko-KR')}건</span>;
                          })()}
                        </td>
                        <td className="nowrap" style={{ fontFamily: 'monospace', color: 'var(--muted)' }}>{m.last_sales_date ?? '-'}</td>
                        <td className="num nowrap" style={daysStyle}>
                          {days == null ? '-' : `${days}일`}
                        </td>
                        <td className="num nowrap">{fmt.date(m.start_date)}</td>
                        <td className="num nowrap">{m.term_days}일</td>
                        <td className="num nowrap" style={
                          m.remaining_days == null ? { color: 'var(--muted)' }
                            : m.remaining_days === 0 ? { color: '#7f1d1d', fontWeight: 700 }
                            : m.remaining_days <= 7 ? { color: '#854d0e', fontWeight: 600 }
                            : undefined
                        }>
                          {m.remaining_days == null ? '-' : `${m.remaining_days}일`}
                        </td>
                        <td className="num nowrap">{fmt.won(m.deposit_amount)}</td>
                        <td className="num nowrap" style={{ fontWeight: 600 }}>{fmt.won(m.purchase_amount)}</td>
                        <td className="num nowrap" style={
                          m.remaining_amount == null ? { color: 'var(--muted)' }
                            : m.remaining_amount === 0 ? { color: 'var(--leaf-700)', fontWeight: 600 }
                            : { fontWeight: 600 }
                        }>
                          {m.remaining_amount == null ? '-' : fmt.won(m.remaining_amount)}
                        </td>
                        <td className="num nowrap">{fmt.pct(m.fee_rate)}</td>
                        <td className="nowrap">
                          {(m.payout_method ?? 'bank') === 'wallet'
                            ? <span className="chip" style={{ background: '#eef3fb', color: '#1e4fc8' }}>지갑정산</span>
                            : <span className="chip" style={{ background: '#f1f5f3', color: 'var(--text-muted)' }}>은행정산</span>}
                        </td>
                        <td className="nowrap">
                          {(m.payout_method ?? 'bank') === 'wallet'
                            ? (m.people_id
                                ? <span style={{ fontFamily: 'monospace' }}>{m.people_id}</span>
                                : <span style={{ color: 'var(--warn)' }}>피플 ID 미입력</span>)
                            : (m.payout_bank ? `${m.payout_bank} ${m.payout_account}` : '-')}
                        </td>
                        <td className="nowrap">{m.payout_holder ?? '-'}</td>
                        <td className="nowrap" style={{ fontSize: 11 }}>
                          {(() => {
                            const st = m.device_return_status ?? 'unreturned';
                            const label = st === 'returned' ? '반납완료' : st === 'not_applicable' ? '해당없음' : '미반납';
                            const color = st === 'returned' ? 'var(--leaf-700)' : st === 'not_applicable' ? 'var(--muted)' : '#c2410c';
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ color, fontWeight: 600 }}>{label}</span>
                                {st === 'returned' && m.device_return_date && (
                                  <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace' }}>{m.device_return_date}</span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="nowrap">
                          <div className="row-actions" style={{ flexWrap: 'wrap', gap: 4 }}>
                            <button className="btn secondary sm" onClick={() => navigate(`/merchants/${encodeURIComponent(m.mid)}`)}>수정</button>
                            <button className="btn secondary sm" onClick={() => toggleActive(m.mid, m.name, m.active ?? 1)} title="활성/비활성 토글">
                              {(m.active ?? 1) ? '비활성' : '활성화'}
                            </button>
                            <button className="btn secondary sm" onClick={() => renameMid(m.mid, m.name)} title="MID 정정 (관련 거래/정산 행의 mcht_id도 함께 갱신)">MID</button>
                            <button className="btn danger sm" onClick={() => remove(m.mid, m.name)}>삭제</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="muted" style={{ marginTop: 12 }}>총 <b style={{ color: 'var(--leaf-800)' }}>{filtered.length}</b>개 가맹점</div>
          </>
        )}
      </div>
      <MerchantDetailModal mid={detailMid} open={!!detailMid} onClose={() => setDetailMid(null)} />

      {/* MID 변경 모달 — 브라우저 prompt 대체 */}
      <Modal
        open={!!renameFor}
        title={renameFor ? `MID 변경 — ${renameFor.name}` : ''}
        onCancel={() => !renaming && setRenameFor(null)}
        onOk={doRename}
        okText="변경"
        okType="danger"
        cancelText="취소"
        confirmLoading={renaming}
        destroyOnHidden
        width={520}
      >
        {renameFor && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
            <div>
              현재 MID: <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{renameFor.mid}</span>
            </div>
            <div>
              <div style={{ marginBottom: 4 }}>새 MID</div>
              <Input value={renameFor.value}
                     onChange={e => setRenameFor(r => r ? { ...r, value: e.target.value } : r)}
                     placeholder="영문/숫자/._- 만 허용"
                     autoFocus
                     onPressEnter={doRename} />
            </div>
            <div style={{ padding: '8px 10px', background: '#fef3c7', color: '#854d0e', borderRadius: 6, fontSize: 12, lineHeight: 1.55 }}>
              관련 <b>거래·정산·이체</b> 행의 mcht_id도 자동 갱신됩니다.
              새 MID가 이미 다른 가맹점으로 등록되어 있으면 <b>거부</b>됩니다 (MID 하나 = 가맹점 하나 절대규칙).
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
